import React, { useState, useEffect } from "react";
import { Button } from '../components/ui/button';
import { useAuth } from "../contexts/AuthContext";
import {
  CheckCircle,
  XCircle,
  Download,
  Eye,
  FileText,
  Receipt,
  Mail,
  Calendar,
  Clock,
  Bell,
  Sparkles,
  Briefcase,
  Wallet,
  Inbox,
} from "lucide-react";
import {
  getEmployees,
  getEmployeeById,
  updateOfferStatus,
  viewOfferLetter,
  downloadJoiningLetter,
  downloadSalarySlip,
  viewJoiningLetter,
  viewSalarySlip,
  getAvailableEmployeeDocuments,
  AvailableEmployeeDocuments,
} from '../api/employees';
import { useToast } from "../hooks/useToast";

interface Employee {
  id: string;
  user_id: string;
  employeeID: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  designation?: string;
  department?: string;
  offer_status?: "Pending" | "Accepted" | "Rejected" | string;
  offer_valid_till?: string;
  offer_date?: string;
  [key: string]: any; // For other properties
  terms_and_conditions?: string; // Add terms_and_conditions to the Employee interface
}

interface OfferLetterCardProps {
  employee: Employee;
  onUpdateStatus: (id: string, status: string) => Promise<void>;
  onDownload: (id: string, tc_id?: string) => Promise<void>;
  onView: (id: string, tc_id?: string) => Promise<void>;
}

/**
 * OfferLetterCard
 * Displays a card with employee name/role and interactive actions
 * based on the current offer_status.
 */
const OfferLetterCard: React.FC<OfferLetterCardProps> = ({ 
  employee, 
  onUpdateStatus, 
  onDownload, 
  onView 
}) => {
  const { toast } = useToast();

  // Log the employee object to see what we're getting
  console.log("OfferLetterCard received employee:", employee);
  console.log("Employee ID in card:", employee?.id);
  console.log("Offer Status:", employee?.offer_status);

  const handleUpdateStatus = async (id: string, status: string) => {
    try {
      console.log("Updating status with ID:", id);
      await onUpdateStatus(id, status);
      toast({
        title: "Success",
        description: `Offer letter ${status} successfully!`,
        variant: "default",
      });
    } catch (error: any) {
      console.error('Error updating status:', error); 
      toast({
        // title: "Error",
        description: error.message || error.detail|| "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async (id: string, termsAndCondition?: string) => {
    try {
      console.log("Downloading with ID:", id);
      await onDownload(id,termsAndCondition);
      toast({
        title: "Success",
        description: "Offer letter downloaded successfully!",
        variant: "default",
      });
    } catch (error: any) {
      console.error('Error downloading offer letter:', error);
      toast({
        // title: "Error",
        description: error.message || error.detail|| "Failed to download offer letter",
        variant: "destructive",
      });
    }
  };

  const handleView = async (id: string) => {
    try {
      console.log("Viewing with ID:", id);
      await onView(id);
      toast({
        title: "Success",
        description: "Offer letter opened successfully!",
        variant: "default",
      });
    } catch (error: any) {
      console.error('Error viewing offer letter:', error);
      toast({
        // title: "Error",
        description: error.message || error.detail|| "Failed to view offer letter",
        variant: "destructive",
      });
    }
  };

  // If employee data is not available yet
  if (!employee) {
    return (
      <div className="border border-gray-200 p-4 rounded-lg bg-white shadow-sm">
        <p className="text-gray-500">Loading employee data...</p>
      </div>
    );
  }

  // Date formatting — show "Jun 6, 2026" style, gracefully ignore empty
  // strings the API may return for unset dates.
  const fmtDate = (raw?: string) => {
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  const issuedOn = fmtDate(employee.offer_date);
  const validTill = fmtDate(employee.offer_valid_till);
  const isOfferGenerated = Boolean(
    employee.offer_status && employee.offer_valid_till && employee.offer_date,
  );

  // Status pill colours mirror the action buttons below for consistency.
  const statusStyles: Record<string, string> = {
    Pending: "bg-amber-100 text-amber-700 border-amber-200",
    Accepted: "bg-green-100 text-green-700 border-green-200",
    Rejected: "bg-red-100 text-red-700 border-red-200",
  };
  const statusKey = (employee.offer_status as string) || "";
  const statusClass =
    statusStyles[statusKey] || "bg-gray-100 text-gray-600 border-gray-200";

  // Add a soft amber glow + "Action needed" pip when the offer is
  // waiting on the employee. Draws the eye without screaming.
  const needsAction = employee.offer_status === "Pending";

  return (
    <div
      className={`relative border rounded-xl bg-white shadow-sm hover:shadow-md transition-all overflow-hidden ${
        needsAction
          ? "border-amber-200 ring-2 ring-amber-100"
          : "border-gray-200"
      }`}
    >
      {needsAction && (
        <span className="absolute -top-2 -right-2 flex h-5 w-5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-5 w-5 bg-amber-500" />
        </span>
      )}
      {/* Accent strip — matches the visual language of the joining
          letter (green) and salary slip (purple) cards while staying
          unique to the offer letter. */}
      <div className="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

      <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Document identity */}
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center text-indigo-700 shrink-0">
            <Mail className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-900 text-base">
                Offer Letter
              </span>
              {employee.offer_status && (
                <span
                  className={`text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${statusClass}`}
                >
                  {employee.offer_status}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              Your formal job offer from the company
            </p>
            {(issuedOn || validTill) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500">
                {issuedOn && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Issued {issuedOn}
                  </span>
                )}
                {validTill && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Valid until {validTill}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action area */}
        <div className="flex flex-wrap gap-2 items-center sm:justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleView(employee.id)}
            disabled={!isOfferGenerated}
            title={
              isOfferGenerated
                ? "View your offer letter"
                : "Your offer letter hasn't been generated yet. Please wait for admin to issue it."
            }
            className="flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Eye className="h-4 w-4" />
            View
          </Button>

          {/* PENDING → Accept / Reject. Same actions as before, just
              laid out beside View instead of beneath it. */}
          {employee.offer_status === "Pending" && (
            <>
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-1"
                onClick={() => handleUpdateStatus(employee.id, "Accepted")}
              >
                <CheckCircle className="h-4 w-4" />
                Accept
              </Button>
              <Button
                size="sm"
                variant="destructive"
                className="flex items-center gap-1"
                onClick={() => handleUpdateStatus(employee.id, "Rejected")}
              >
                <XCircle className="h-4 w-4" />
                Reject
              </Button>
            </>
          )}

          {/* ACCEPTED → Download */}
          {employee.offer_status === "Accepted" && (
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1"
              onClick={() =>
                handleDownload(employee.id, employee.terms_and_conditions)
              }
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Small profile strip rendered at the top of the page so each
// document card can focus on the document, not on identity. Single
// source of truth for employee name / role / ID.
const EmployeeIdentityHeader: React.FC<{ employee: Employee }> = ({
  employee,
}) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-center gap-4">
    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xl font-semibold shrink-0">
      {(employee.first_name?.charAt(0) ||
        employee.employeeID?.charAt(0) ||
        "E").toUpperCase()}
    </div>
    <div className="min-w-0">
      <div className="font-semibold text-gray-900 text-base truncate">
        {employee.first_name} {employee.last_name}
      </div>
      <div className="text-sm text-gray-500 truncate">
        {employee.designation || "Position"} ·{" "}
        {employee.department || "Department"}
      </div>
      {employee.employeeID && (
        <div className="text-xs text-gray-400 mt-0.5">
          Employee ID · {employee.employeeID}
        </div>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------
// Joining Letter card. Only rendered when the admin has actually
// generated / sent a joining letter for the employee — driven by the
// joining_letter.available flag from /employee_documents/available.
// Mirrors the Offer Letter card with two actions: View opens the PDF
// in a new tab, Download saves it.
// ---------------------------------------------------------------------
const JoiningLetterCard: React.FC<{
  employee: Employee;
  sentAt: string | null;
}> = ({ employee, sentAt }) => {
  const { toast } = useToast();
  const [viewBusy, setViewBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);

  const handleView = async () => {
    setViewBusy(true);
    try {
      await viewJoiningLetter(employee.id);
    } catch (err: any) {
      toast({
        description:
          err?.message || err?.detail || "Failed to open joining letter",
        variant: "destructive",
      });
    } finally {
      setViewBusy(false);
    }
  };

  const handleDownload = async () => {
    setDownloadBusy(true);
    try {
      // probationPeriod / tc_id come from the admin's persisted
      // configuration server-side; the employee call doesn't pass any
      // overrides so the backend renders against the saved values.
      await downloadJoiningLetter(employee.id);
      toast({
        title: "Success",
        description: "Joining letter downloaded successfully!",
      });
    } catch (err: any) {
      toast({
        description:
          err?.message || err?.detail || "Failed to download joining letter",
        variant: "destructive",
      });
    } finally {
      setDownloadBusy(false);
    }
  };

  const sentLabel = sentAt
    ? new Date(sentAt).toLocaleDateString()
    : null;

  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-all overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-emerald-500 to-green-500" />
      <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center text-green-700 shrink-0">
            <FileText className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <span className="font-semibold text-gray-900 text-base block">
              Joining Letter
            </span>
            <p className="text-sm text-gray-500 mt-0.5">
              Your formal onboarding letter
            </p>
            {sentLabel && (
              <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                <Calendar className="h-3 w-3" />
                Issued {sentLabel}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center sm:justify-end">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            onClick={handleView}
            disabled={viewBusy}
          >
            <Eye className="h-4 w-4" />
            {viewBusy ? "Opening…" : "View"}
          </Button>
          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-1"
            onClick={handleDownload}
            disabled={downloadBusy}
          >
            <Download className="h-4 w-4" />
            {downloadBusy ? "Downloading…" : "Download"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Salary Slip cards — one row per (month, year) the admin has generated
// for the employee. The list comes from /employee_documents/available;
// no row → no card. Each card has View + Download actions.
// ---------------------------------------------------------------------
const SalarySlipRow: React.FC<{
  employee: Employee;
  month: number;
  year: number;
}> = ({ employee, month, year }) => {
  const { toast } = useToast();
  const [viewBusy, setViewBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const label = `${months[month - 1] ?? month} ${year}`;

  const handleView = async () => {
    setViewBusy(true);
    try {
      await viewSalarySlip(employee.id, month, year);
    } catch (err: any) {
      toast({
        description:
          err?.message || err?.detail || "Failed to open salary slip",
        variant: "destructive",
      });
    } finally {
      setViewBusy(false);
    }
  };

  const handleDownload = async () => {
    setDownloadBusy(true);
    try {
      await downloadSalarySlip(employee.id, month, year);
      toast({
        title: "Success",
        description: `Salary slip for ${label} downloaded successfully!`,
      });
    } catch (err: any) {
      toast({
        description:
          err?.message || err?.detail || "Failed to download salary slip",
        variant: "destructive",
      });
    } finally {
      setDownloadBusy(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-all overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-purple-500 to-fuchsia-500" />
      <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-fuchsia-100 flex items-center justify-center text-purple-700 shrink-0">
            <Receipt className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <span className="font-semibold text-gray-900 text-base block">
              Salary Slip · {label}
            </span>
            <p className="text-sm text-gray-500 mt-0.5">
              Pay statement for {label}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center sm:justify-end">
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
            onClick={handleView}
            disabled={viewBusy}
          >
            <Eye className="h-4 w-4" />
            {viewBusy ? "Opening…" : "View"}
          </Button>
          <Button
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1"
            onClick={handleDownload}
            disabled={downloadBusy}
          >
            <Download className="h-4 w-4" />
            {downloadBusy ? "Downloading…" : "Download"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Parent component
// ---------------------------------------------------------------------
export default function OfferLetterList() {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Backend snapshot of which optional documents the admin has issued
  // for this employee — controls whether the Joining Letter card and
  // any Salary Slip rows render. null = not loaded yet; empty fields
  // = nothing issued.
  const [availableDocs, setAvailableDocs] =
    useState<AvailableEmployeeDocuments | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        
        if (!user) {
          console.log("No user found");
          setLoading(false);
          return;
        }

        console.log("Current user from auth:", user);

        // Step 1: Get all employees to find the matching employee ID
        const response = await getEmployees();
        const allEmployees = response.employees || [];

        console.log("All employees from API:", allEmployees);

        // Step 2: Find the employee where user_id matches the logged-in
        // user's ID. Fall back to email matching in case the user_id link
        // is stale or the employee was created with a different identifier
        // (e.g. an HR head whose account predates the linking change).
        const userEmail = ((user as any)?.email || "").toString().toLowerCase();
        const currentEmployee =
          allEmployees.find((emp: any) => emp.user_id === user.id) ||
          (userEmail
            ? allEmployees.find(
                (emp: any) =>
                  (emp.email || "").toString().toLowerCase() === userEmail,
              )
            : undefined);

        console.log("Found matching employee:", currentEmployee);
        
        if (currentEmployee && currentEmployee.id) {
          // Step 3: Use the employee ID to fetch complete details via getEmployeeById
          const employeeId = currentEmployee.id;
          console.log("Using employee ID to fetch details:", employeeId);
          
          const detailsResponse = await getEmployeeById(employeeId);
          console.log("Employee details response:", detailsResponse);
          
          // Navigate through the nested structure to get the actual employee object
          // Background fetches shouldn't fire success toasts — that's
          // pure noise and was contributing to the misleading "Success"
          // popups elsewhere. Just set the employee silently.
          let resolvedEmployee: Employee | null = null;
          if (detailsResponse &&
              detailsResponse.employee &&
              detailsResponse.employee.employee) {
            resolvedEmployee = detailsResponse.employee.employee as Employee;
          } else if (detailsResponse && detailsResponse.employee && detailsResponse.employee.id) {
            resolvedEmployee = detailsResponse.employee as Employee;
          } else if (detailsResponse && detailsResponse.id) {
            resolvedEmployee = detailsResponse as Employee;
          } else {
            console.error("Unexpected response structure:", detailsResponse);
            // Leave `employee` null and let the empty-state UI handle it.
          }
          setEmployee(resolvedEmployee);

          // Once we know the employee row, ask the backend which
          // optional documents the admin has issued. Failure here is
          // non-fatal — the page still works for the offer letter.
          if (resolvedEmployee?.id) {
            try {
              const docs = await getAvailableEmployeeDocuments(
                resolvedEmployee.id,
              );
              setAvailableDocs(docs);
            } catch (docsErr) {
              console.warn(
                "Failed to load available documents — joining letter / salary slip cards will be hidden.",
                docsErr,
              );
              setAvailableDocs({
                joining_letter: { available: false, sent_at: null },
                salary_slips: [],
              });
            }
          }
        } else {
          // No employee row for this user — common case for the workspace
          // admin who registered the account but isn't an employee. Leave
          // `employee` null; the render below shows a friendly empty
          // state rather than a red error block.
          console.log(
            "No employee record found for this user — showing empty state.",
          );
        }
      } catch (error: any) {
        console.error("Employee fetch failed", error);
        setError(error.message || error.detail || "Failed to fetch employee data");
        toast({
          // title: "Error",
          description: error.message || error.detail || "Failed to fetch employee data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [user, toast]);

  // Update offer status
  const handleUpdateStatus = async (id: string, status: string) => {
    try {
      console.log(`Updating status for employee ${id} to ${status}`);
      if (!id) {
        console.error("No employee ID provided");
        toast({
          // title: "Error",
          description: "Cannot update status: No employee ID available",
          variant: "destructive",
        });
        return;
      }
      if (!status) {
        console.error("No status provided");
        toast({
          // title: "Error",
          description: "Cannot update status: No status provided",
          variant: "destructive",
        });
        return;
      }
      
      const response = await updateOfferStatus(id, status);
      
      // Update local state to reflect the change
      setEmployee(prevEmployee => 
        prevEmployee && prevEmployee.id === id 
          ? { ...prevEmployee, offer_status: status } 
          : prevEmployee
      );
  
      console.log(`Status updated to ${status}`, response);
      toast({
        title: "Success",
        description: `Offer letter ${status} successfully!`,
        variant: "default",
      });
      
    } catch (err: any) {
      console.error('Error updating status:', err);
      toast({
        // title: "Error",
        description: err.message || err.detail || "Failed to update status",
        variant: "destructive",
      });
    }
  };

  // Download offer letter
  const handleDownload = async (id: string) => {
    try {
      console.log(`Downloading offer letter for employee:`, id);
      if (!id) {
        console.error("No employee ID provided");
        toast({
          // title: "Error",
          description: "Cannot download: No employee ID available",
          variant: "destructive",
        });
        return;
      }
      await viewOfferLetter(id);
      toast({
        title: "Success",
        description: "Offer letter downloaded successfully!",
        variant: "default",
      });
    } catch (err: any) {
      console.error('Error downloading offer letter:', err);
      toast({
        // title: "Error",
        description: err.message || err.detail || "Failed to download offer letter",
        variant: "destructive",
      });
    }
  };

  // View offer letter
  const handleView = async (employeeId: string, termsAndCondition?: string) => {
    try {
      console.log(`Viewing offer letter for employee:`, employeeId);
      if (!employeeId) {
        console.error("No employee ID provided");
        toast({
          // title: "Error",
          description: "Cannot view: No employee ID available",
          variant: "destructive",
        });
        return;
      }
      await viewOfferLetter(employeeId, termsAndCondition || "");
      toast({
        title: "Success",
        description: "Offer letter opened successfully!",
        variant: "default",
      });
    } catch (err: any) {
      console.error('Error viewing offer letter:', err);
      toast({
        // title: "Error",
        description: err.message || err.detail || "Failed to view offer letter",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-6 bg-gray-50 min-h-screen">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-6 bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error: {error}
        </div>
      </div>
    );
  }

  // Derive interaction signals once so the welcome bar and section
  // headers can reflect the real state without duplicating logic.
  const firstName = employee?.first_name?.trim() || "there";
  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  })();
  const offerPending = employee?.offer_status === "Pending";
  const joiningAvailable = !!availableDocs?.joining_letter?.available;
  const slipCount = availableDocs?.salary_slips?.length ?? 0;
  const totalDocs =
    (employee ? 1 : 0) + (joiningAvailable ? 1 : 0) + slipCount;
  const actionsNeeded = (offerPending ? 1 : 0);

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-5 bg-gradient-to-b from-gray-50 to-white min-h-screen">
      {/* Personalised welcome strip — replaces the static "My
          Documents" heading with something the employee actually
          looks at. Highlights how many docs are ready and whether
          anything is waiting on them. */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-indigo-500" />
            {greeting}, {firstName}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Here are your documents and what needs your attention.
          </p>
        </div>
        {employee && (
          <div className="flex items-center gap-3">
            <div className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-center shadow-sm">
              <div className="text-lg font-semibold text-gray-900">
                {totalDocs}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">
                Documents
              </div>
            </div>
            <div
              className={`rounded-lg px-3 py-1.5 text-center shadow-sm border ${
                actionsNeeded > 0
                  ? "bg-amber-50 border-amber-200"
                  : "bg-white border-gray-200"
              }`}
            >
              <div
                className={`text-lg font-semibold ${
                  actionsNeeded > 0 ? "text-amber-700" : "text-gray-900"
                }`}
              >
                {actionsNeeded}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500">
                Need action
              </div>
            </div>
          </div>
        )}
      </div>

      {!employee ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-3">
            <Inbox className="h-7 w-7" />
          </div>
          <p className="text-gray-800 font-medium mb-1">
            No documents linked to your account
          </p>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Your account isn't tied to an employee record yet, so there's no
            paperwork to show here. If you think this is wrong, please ask your
            admin to add you as an employee.
          </p>
        </div>
      ) : (
        <>
          <EmployeeIdentityHeader employee={employee} />

          {/* Action-needed banner — only when the offer is pending.
              Gives the user a single-glance summary of "what should
              I do next" rather than making them parse three cards. */}
          {offerPending && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-sm">
              <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <Bell className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  Your offer letter is waiting for your response
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Review and accept or reject below. Once accepted, you'll be
                  able to download your offer.
                </p>
              </div>
            </div>
          )}

          {/* Section: Onboarding — offer & joining letter live here. */}
          <SectionHeader
            icon={<Briefcase className="h-4 w-4" />}
            title="Onboarding"
            subtitle="The paperwork you got when you joined."
          />
          <OfferLetterCard
            key={employee.id}
            employee={employee}
            onUpdateStatus={handleUpdateStatus}
            onDownload={handleDownload}
            onView={handleView}
          />
          {joiningAvailable ? (
            <JoiningLetterCard
              employee={employee}
              sentAt={availableDocs!.joining_letter.sent_at}
            />
          ) : (
            <EmptyDocPlaceholder
              icon={<FileText className="h-5 w-5" />}
              title="Joining letter not issued yet"
              hint="It'll show up here as soon as HR generates and sends it."
            />
          )}

          {/* Section: Pay — salary slips, one per generated period. */}
          <SectionHeader
            icon={<Wallet className="h-4 w-4" />}
            title="Pay"
            subtitle={
              slipCount === 0
                ? "No payslips yet."
                : `${slipCount} payslip${slipCount === 1 ? "" : "s"} available.`
            }
          />
          {slipCount > 0 ? (
            (availableDocs!.salary_slips).map((slip) => (
              <SalarySlipRow
                key={`${slip.year}-${slip.month}`}
                employee={employee}
                month={slip.month}
                year={slip.year}
              />
            ))
          ) : (
            <EmptyDocPlaceholder
              icon={<Receipt className="h-5 w-5" />}
              title="No salary slips yet"
              hint="Each month's slip appears here once HR runs payroll."
            />
          )}
        </>
      )}
    </div>
  );
}

// Small section divider used between Onboarding and Pay groupings so
// the page reads as a structured doc hub instead of an unbroken card
// list. Icon + title + subtitle keeps it scannable.
const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
}> = ({ icon, title, subtitle }) => (
  <div className="flex items-center gap-2 pt-2">
    <div className="w-6 h-6 rounded-md bg-gray-100 text-gray-600 flex items-center justify-center">
      {icon}
    </div>
    <div className="flex items-baseline gap-2 flex-wrap">
      <span className="text-sm font-semibold text-gray-800">{title}</span>
      {subtitle && (
        <span className="text-xs text-gray-500">{subtitle}</span>
      )}
    </div>
  </div>
);

// Compact placeholder shown in lieu of a real document card when the
// admin hasn't generated that document type yet. Communicates "nothing
// to see yet, here's why" without breaking the visual rhythm.
const EmptyDocPlaceholder: React.FC<{
  icon: React.ReactNode;
  title: string;
  hint: string;
}> = ({ icon, title, hint }) => (
  <div className="border border-dashed border-gray-200 rounded-xl bg-gray-50/50 p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 text-gray-400 flex items-center justify-center shrink-0">
      {icon}
    </div>
    <div>
      <p className="text-sm font-medium text-gray-700">{title}</p>
      <p className="text-xs text-gray-500 mt-0.5">{hint}</p>
    </div>
  </div>
);